import React from "react";
import { View, StyleSheet, Platform } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NavigationProp } from "@react-navigation/native";
import type { RootStackParamList } from "../../../navigation/navigation.types";
import { Course, useEvaluateCourseMutation } from "../../../graphql/generated/graphql";
import CourseEvaluationCard from "../../../components/adminDashboard/CourseEvaluationCard/CourseEvaluationCard";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { formatTimeAgo, formatSemester } from "../../../utils/date";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";

interface CourseCardItemProps {
  course: Course;
}

interface CourseEvaluationCardContainerProps {
  courses: Course[];
  refetchCourses?: () => void;
}

interface CourseCardItemWithRefetchProps extends CourseCardItemProps {
  refetchCourses?: () => void;
}

const CourseCardItem: React.FC<CourseCardItemWithRefetchProps> = ({ course, refetchCourses }) => {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const [evaluateCourse, { loading: evaluating }] = useEvaluateCourseMutation();

  // Find the latest completed evaluation from the evaluations array
  const latestCompletedEvaluation = course.evaluations
    ?.filter((evaluation) => evaluation.status === "completed" && evaluation.createdAt)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

  const evaluatedDate = latestCompletedEvaluation?.createdAt
    ? new Date(latestCompletedEvaluation.createdAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : undefined;

  // Check if there's a pending evaluation
  const hasPendingEvaluation = course.evaluations?.some((evaluation) => evaluation.status === "pending") ?? false;

  const courseDate = course.startDate ? formatSemester(course.startDate) : undefined;
  const studentCount = course.enrolledStudents?.length || 0;

  const assignmentCount = course.hasAssignmentAssignments?.length || 0;
  const formattedLastSynced = course.last_modified ? formatTimeAgo(course.last_modified) : undefined;

  const handleViewEvaluation = () => {
    navigation.navigate("Course Evaluator Details", { courseId: course.id });
  };

  const handleEvaluate = async () => {
    // Prevent starting a new evaluation if one is already pending
    if (hasPendingEvaluation) {
      return;
    }

    try {
      await evaluateCourse({ variables: { courseId: course.id } });
      // Immediately refetch courses to show pending status
      if (refetchCourses) {
        refetchCourses();
      }
      // Optionally navigate to the evaluation detail view after starting
      navigation.navigate("Course Evaluator Details", { courseId: course.id });
    } catch (err) {
      console.log("Error starting evaluation", err);
    }
  };

  return (
    <CourseEvaluationCard
      title={course.name || ""}
      courseCode={course.course_code || ""}
      courseDate={courseDate}
      studentCount={studentCount}
      assignmentCount={assignmentCount}
      lastSynced={formattedLastSynced}
      evaluatedDate={evaluatedDate}
      isPending={hasPendingEvaluation}
      evaluating={evaluating}
      onViewEvaluation={handleViewEvaluation}
      onEvaluate={handleEvaluate}
    />
  );
};

const CourseEvaluationCardContainer: React.FC<CourseEvaluationCardContainerProps> = ({ courses, refetchCourses }) => {
  const { width } = useResponsiveScreen();
  const isSmallScreen = width <= 770;

  if (courses.length === 0) {
    return <CustomText text="No courses found" size="m" />;
  }

  const containerStyle = [
    styles.container,
    Platform.OS === "web" && {
      gridTemplateColumns: isSmallScreen ? "1fr" : "repeat(2, 1fr)",
    },
  ];

  return (
    <View style={containerStyle}>
      {courses.map((course) => (
        <View key={course.id} style={styles.cardWrapper}>
          <CourseCardItem course={course} refetchCourses={refetchCourses} />
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    ...(Platform.OS === "web"
      ? ({
          display: "grid",
          gap: 20,
          width: "100%",
        } as any)
      : {
          flexDirection: "column",
          gap: 20,
        }),
  },
  cardWrapper: {
    ...(Platform.OS === "web"
      ? ({
          width: "100%",
        } as any)
      : {}),
  },
});

export default CourseEvaluationCardContainer;
